import {
  AlertType,
  IAlert,
  Maybe,
  ICompanyUserNotificationUpdateMutationVariables,
  ICompanyUserNotificationUpdateMutationResult,
  ICompanyUserInput,
} from '../../graphql/schema/p4types';
import React, { CSSProperties, ReactNode, useContext, useEffect } from 'react';
import styled from 'styled-components';
import { NotificationMessageList } from './Notification';
import iconMixin from '../../styles/utils/iconMixin';
import ConnectedFormContext from '../Context/ConnectedFormContext';
import { p4Tokens } from '../../styles/themes/default/p4FluentTheme';
import { useMutation } from '@apollo/client';
import CompanyUserNotificationUpdate from '../../graphql/mutations/company/CompanyUserNotificationMutation.graphql';
import getClientMutationId from '../../utils/getClientMutationId';
import UserContext from '../Context/UserContext';

export const NotificationTitle = styled.div`
  color: #1c2024;
  font-size: 16px;
  font-weight: 600;
  display: block;
`;

const NotificationMessageContainer = styled.div`
  display: block;
  float: left;
  padding-top: 5px;
`;

export const NotificationMessage = styled.div`
  color: ${(props) => props.theme.colors.notificationText};
  font-size: 15px;
  display: block;
  max-height: 135px;
  overflow: auto;
`;

const NotificationDismissHeader = styled.div`
  cursor: pointer;
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
  width: 100%;
  padding-bottom: 10px;
  padding-right: 10px;
`;

type INotificationIconProps = { type?: Maybe<AlertType>; style?: CSSProperties; fontSize?: string };

const notificationTypeColor = ({ type, theme }: { type: Maybe<AlertType>; theme: any }) => {
  switch (type) {
    case AlertType.Success:
      return p4Tokens.statusFlagSuccess;
    case AlertType.Info:
    case AlertType.IndeterminateProgress:
    case AlertType.Action:
      return p4Tokens.statusFlagInfo;
    case AlertType.Warning:
      return p4Tokens.statusFlagWarning;
    case AlertType.Error:
    default:
      return p4Tokens.statusFlagDanger;
  }
};

export const NotificationIcon = styled.div<INotificationIconProps>`
  ${(props: INotificationIconProps) => iconMixin(props.fontSize || '30px')};
  color: ${(props: INotificationIconProps & { theme: any }) =>
    notificationTypeColor({ type: props.type, theme: props.theme })};
  float: left;
`;

export const setNotificationIcon = (type?: AlertType | null) => {
  switch (type) {
    case AlertType.Success:
      return 'check_circle';
    case AlertType.Info:
      return 'info';
    case AlertType.Error:
    case AlertType.Warning:
    default:
      return 'error';
  }
};

export const notificationBackgroundColours = {
  [AlertType.Warning]: '#FFF5EA',
  [AlertType.Success]: '#F6F6F7',
  [AlertType.Info]: '#EBF9FC',
};

export const notificationBorderColours = {
  [AlertType.Warning]: '#E1B878',
  [AlertType.Success]: '#BABFC3',
  [AlertType.Info]: '#98C6CD',
};

const NotificationRow = styled.div<{ style?: CSSProperties; type: Maybe<AlertType> }>`
  box-sizing: border-box;
  display: flex;
  flex: 0 1 auto;
  flex-direction: row;
  flex-wrap: wrap;
  margin: 7px;
  background-color: ${(props: any) =>
    notificationBackgroundColours[props.type] || notificationBackgroundColours[AlertType.Success]};
  border: solid 1px
    ${(props: any) => notificationBorderColours[props.type] || notificationBorderColours[AlertType.Success]};
  border-radius: 5px;

  @media (max-width: 960px) {
    flex-direction: column;
  }
`;

const NotificationContentContainer = styled.div`
  padding: 3px 14px 7px;
  width: 100%;
  border-bottom: 1px solid #e5e5e5;
  position: relative;
`;

/**
 * Similar to Notification/DismissedNotification, but design adjusted for display in the header.
 * @param alert - Alert entity to be displayed.
 * @param styles - Style overrides for the alert.
 * @param dismiss - If provided, used to dismiss the alert (Expected usage is "Block" notifications - "Header" notifications dismiss via connectedFormContext).
 */
export const HeaderNotification = ({
  alert,
  styles = {},
  dismiss,
}: {
  alert: IAlert;
  styles?: { row?: CSSProperties };
  dismiss?: () => void;
}) => {
  const { title, message, alertType } = alert;
  const connectedForm = useContext(ConnectedFormContext);
  const { currentUser } = useContext(UserContext);

  const messages: ReactNode[] = [];
  message?.split('\n').forEach((msg) => {
    messages.push(<li key={msg}>{msg}</li>);
  });

  const iconStyle = setNotificationIcon(alertType);

  const onDismiss = () => {
    if (dismiss) {
      dismiss();
    } else {
      connectedForm.removeHeaderAlert?.(alert);
    }
  };

  const [notificationUpdate] = useMutation<
    ICompanyUserNotificationUpdateMutationResult,
    ICompanyUserNotificationUpdateMutationVariables
  >(CompanyUserNotificationUpdate, {
    errorPolicy: 'ignore',
    update: (dataProxy, { data }) => {
      if (!data?.companyUserNotificationUpdate?.result) return;

      dataProxy.modify({
        fields: {
          companyUserNotifications: (existingData, { DELETE }) => {
            return DELETE;
          },
        },
      });
    },
  });

  useEffect(() => {
    notificationUpdate({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          companyUserNotification: {
            title: alert.title ?? 'Untitled',
            message: message ?? 'No message provided',
            alertType: alertType as AlertType,
            companyUser: { id: currentUser.companyUser?.id } as ICompanyUserInput,
          },
        },
      },
    });
  }, [alert.title, alertType, currentUser.companyUser?.id, message, notificationUpdate]);

  return (
    <NotificationRow key={`${title}${message}`} style={styles.row} type={alertType}>
      <NotificationContentContainer>
        <NotificationIcon type={alertType} style={{ paddingTop: '9px' }} fontSize={'20px'}>
          {iconStyle}
        </NotificationIcon>
        <NotificationMessageContainer
          style={{
            width: '100%',
            position: 'relative',
            overflow: 'auto',
            wordWrap: 'break-word',
            whiteSpace: 'normal',
          }}
        >
          <NotificationTitle data-testid="notification-title">{title}</NotificationTitle>
          <NotificationMessage>
            {messages.length > 1 ? <NotificationMessageList>{messages}</NotificationMessageList> : message}
          </NotificationMessage>
        </NotificationMessageContainer>
      </NotificationContentContainer>
      <NotificationDismissHeader onClick={onDismiss}>DISMISS</NotificationDismissHeader>
    </NotificationRow>
  );
};
